import { Users, Globe, BarChart3, HeartHandshake } from "lucide-react";
import { LogoLockup } from "@/components/brand/Logo";
import { SolanaLogo } from "@/components/brand/SolanaLogo";
import { SocialLinks } from "@/components/SocialLinks";
import { BRAND, COMMUNITY, LINKS } from "@/config/kingbullish";
import { useTokenData } from "@/context/TokenDataContext";
import { formatInt } from "@/lib/format";
import { Reveal } from "@/components/motion/Reveal";

const Stat = ({ icon: Icon, value, label, testId }) => (
  <div className="flex items-center gap-3" data-testid={testId}>
    <span className="grid h-11 w-11 shrink-0 place-items-center rounded-md border border-neon/40 bg-neon/5 text-neon">
      <Icon className="h-5 w-5" />
    </span>
    <div className="leading-none">
      <div className="font-display text-[0.95rem] font-bold text-white">{value}</div>
      <div className="mt-1 font-body text-[0.62rem] font-semibold tracking-[0.2em] text-white/60">{label}</div>
    </div>
  </div>
);

export const CommunityFooter = () => {
  const { token } = useTokenData();
  const holders = token ? formatInt(token.holders) : "—";

  return (
    <footer id="community" className="relative z-10 border-t border-neon/15 bg-gradient-to-b from-transparent to-black/60" data-testid="community-footer">
      <div className="container-x">
        <section className="border-b border-gold/15 py-10" aria-labelledby="community-heading" data-testid="community-social-area">
          <h2 id="community-heading" className="font-display text-base font-bold leading-relaxed text-neon md:text-lg" data-testid="community-heading">
            JOIN THE KINGBULLISH COMMUNITY
          </h2>
          <p className="mt-2 font-body text-sm text-white/70 md:text-base" data-testid="community-description">
            Follow KINGBULLISH across the official channels.
          </p>
          <SocialLinks placement="community" labelled className="mt-5" />
        </section>

        <Reveal className="grid gap-10 py-14 lg:grid-cols-[auto_1fr_auto] lg:items-center lg:gap-12">
          <LogoLockup size={60} testId="footer-logo" />

          <div className="grid grid-cols-2 gap-6 md:grid-cols-4" data-testid="footer-stats">
            <Stat icon={Users} value={holders} label="HOLDERS" testId="footer-stat-holders" />
            <Stat icon={Globe} value={COMMUNITY.countries ?? "—"} label="COUNTRIES" testId="footer-stat-countries" />
            <Stat icon={BarChart3} value="GLOBAL" label="REACH" testId="footer-stat-global-reach" />
            <Stat icon={HeartHandshake} value="A STRONG" label="COMMUNITY" testId="footer-stat-community" />
          </div>

          <div className="flex flex-wrap items-center gap-6 lg:flex-col lg:items-end">
            <SocialLinks placement="footer" compact />
            <div className="glass flex items-center gap-2.5 rounded-lg px-3 py-2" data-testid="footer-powered-by-solana">
              <SolanaLogo size={20} />
              <div className="leading-none">
                <div className="font-body text-[0.55rem] font-semibold tracking-[0.22em] text-white/55">POWERED BY</div>
                <div className="mt-0.5 font-display text-[0.8rem] font-bold tracking-wider text-white">SOLANA</div>
              </div>
            </div>
          </div>
        </Reveal>

        <p className="pb-8 font-body text-xs font-medium leading-relaxed text-white/40" data-testid="footer-disclaimer">
          $KBULL is a utility token on the Solana blockchain. No account, wallet connection, password, seed phrase or private key is required to use this website. KINGBULLISH never asks for private keys and does not custody funds. Nothing here is financial advice; no profits or returns are promised. Digital assets are volatile — do your own research.
        </p>

        <div className="flex flex-col gap-5 border-t border-white/10 py-7 md:flex-row md:items-center md:justify-between">
          <div className="font-body text-[0.68rem] font-semibold tracking-[0.18em] text-white/55" data-testid="footer-copyright">
            © {new Date().getFullYear()} {BRAND.name}. ALL RIGHTS RESERVED.
          </div>
          <nav className="flex flex-wrap items-center gap-x-6 gap-y-2" aria-label="Legal" data-testid="footer-legal-links">
            <a href={LINKS.privacy} className="footer-link" data-testid="footer-link-privacy">Privacy Policy</a>
            <a href={LINKS.terms} className="footer-link" data-testid="footer-link-terms">Terms of Service</a>
            <a href={LINKS.contact} className="footer-link" data-testid="footer-link-contact">Contact</a>
          </nav>
          <div className="font-display text-[0.72rem] font-bold tracking-[0.12em] text-white/80 md:text-right" data-testid="footer-tagline">
            {BRAND.supporting[0]} <span className="text-neon glow-green">{BRAND.supporting[1]}</span>
          </div>
        </div>
      </div>
    </footer>
  );
};
